/**
 * Synthetic examples of the ledger, session, catalogue, Inbox and hygiene routes (1.0b
 * deliverables 19 and 20), set in "The Drowned Lanterns" on `p1b`: the three sessions, the
 * tentative S3 event, Maelis Dorn's secret, the stub Guard-Captain Rell, Dockhand 39 merged into
 * Dockhand 40, and Old Sef named in plain text in Missing Lamplighters.
 */
import { docMetaFrom } from '../dto/documents.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_ANSWER_IDS, EXAMPLE_NOW } from './examples.js';
import { EXAMPLE_PLACEMENT, EXAMPLE_UNPLACED_ROW } from './examples-chronology.js';
import { EXAMPLE_TYPE_IDS, exampleDocument } from './examples-docs.js';
import { EXAMPLE_UNLINKED } from './examples-workspace.js';
const ids = EXAMPLE_IDS;
const answer = EXAMPLE_ANSWER_IDS;
const ref = (id, fid, title, typeKey) => ({
    id,
    fid,
    title,
    titlePending: false,
    typeKey,
    library: 'canon',
    isStub: false,
});
/** Maelis Dorn's secret, partly known since session 2 (S2). */
export const EXAMPLE_SECRET = {
    id: answer.secretMaelis,
    fid: 'SecretMaelisS002',
    rv: 1,
    documentId: ids.npcMaelisDorn.id,
    blockId: answer.secretMaelisBlock,
    label: 'Her ledger',
    text: 'Maelis keeps a second ledger of the Silt Guild’s debts.',
    trigger: 'If the party searches her counting room',
    orphanedAt: null,
    retiredAt: null,
    state: 'partial',
    firstKnownSessionId: ids.sessionSaltInTheWells.id,
    knownBeforeTracking: false,
    createdAt: EXAMPLE_NOW,
    updatedAt: EXAMPLE_NOW,
};
/** What `knowledge.create`'s example records: the Tidebell known to Tamsin Reed in session 2. */
export const EXAMPLE_KNOWLEDGE_CREATED = {
    id: answer.knowledgeCreated,
    secretId: null,
    documentId: ids.itemTidebell.id,
    action: 'assert',
    state: 'known',
    tentative: false,
    supersedesId: null,
    sessionId: ids.sessionSaltInTheWells.id,
    beforeTracking: false,
    at: null,
    pcIds: [ids.pcTamsinReed.id],
    how: 'Tamsin rang it on the Old Quay.',
    origin: 'manual',
    foldSessionNo: 2,
    foldTick: null,
    createdAt: EXAMPLE_NOW,
};
/** `knowledge.retract`'s answer: the retraction of the tentative S3 event, superseding it. */
export const EXAMPLE_KNOWLEDGE_RETRACTED = {
    ...EXAMPLE_KNOWLEDGE_CREATED,
    id: answer.knowledgeRetracted,
    secretId: answer.secretS3,
    documentId: null,
    action: 'retract',
    state: null,
    supersedesId: ids.knowledgeS3Tentative.id,
    sessionId: ids.sessionSunkenChapel.id,
    foldSessionNo: 3,
    pcIds: [],
    how: null,
};
/** Missing Lamplighters, played in session 1. */
export const EXAMPLE_PLAY_RECORD = {
    id: answer.playMissingLamplighters,
    documentId: ids.questMissingLamplighters.id,
    blockId: null,
    sessionId: ids.sessionLampsOutOnTheQuay.id,
    beforeTracking: false,
    state: 'played',
    outcomeNote: null,
    supersedesId: null,
    origin: 'manual',
    foldSessionNo: 1,
    createdAt: EXAMPLE_NOW,
};
/** What `play.create`'s example records: The Sunken Chapel played in session 3. */
export const EXAMPLE_PLAY_CREATED = {
    ...EXAMPLE_PLAY_RECORD,
    id: answer.playCreated,
    documentId: ids.questSunkenChapel.id,
    sessionId: ids.sessionSunkenChapel.id,
    outcomeNote: 'The party bargained instead of fighting.',
    foldSessionNo: 3,
};
/**
 * The undated placement with role `session` that `sessions.create` writes through 1.L-2 (origin
 * `session`, no session id: the session is the document; 1.T item T2), unplaced until the GM dates
 * it with `placements.update`.
 */
const sessionPlacement = (id, documentId) => ({
    ...EXAMPLE_UNPLACED_ROW.placement,
    id,
    documentId,
    role: 'session',
    origin: 'session',
});
/** Session 3, "The Sunken Chapel": prepped, with an undated in-world span. */
export const EXAMPLE_SESSION = {
    document: ref(ids.sessionSunkenChapel.id, 'SessionChapel003', 'The Sunken Chapel', 'session'),
    number: 3,
    realDate: '2026-09-12',
    status: 'prepped',
    origin: 'live',
    startedAt: null,
    endedAt: null,
    placement: sessionPlacement(answer.placementSession3, ids.sessionSunkenChapel.id),
};
/** Session 2, "Salt in the Wells": played, its span in 9 Ember 1042 AL. */
export const EXAMPLE_SESSION_PLAYED = {
    document: ref(ids.sessionSaltInTheWells.id, 'SessionSalt00002', 'Salt in the Wells', 'session'),
    number: 2,
    realDate: '2026-09-05',
    status: 'played',
    origin: 'live',
    startedAt: '2026-09-05T23:00:00.000Z',
    endedAt: '2026-09-06T03:30:00.000Z',
    placement: {
        ...EXAMPLE_PLACEMENT,
        id: answer.placementSession2,
        documentId: ids.sessionSaltInTheWells.id,
        role: 'session',
        origin: 'session',
    },
};
/** The session `sessions.create`'s example makes: planned, with no number yet. */
export const EXAMPLE_SESSION_CREATED = {
    document: ref(answer.sessionCreated, 'SessionBargain04', "The Abbot's Bargain", 'session'),
    number: null,
    realDate: null,
    status: 'planned',
    origin: 'live',
    startedAt: null,
    endedAt: null,
    placement: sessionPlacement(answer.placementSessionCreated, answer.sessionCreated),
};
/** What `sessions.update`'s example answers: session 3 moved to the next week's game. */
export const EXAMPLE_SESSION_UPDATED = { ...EXAMPLE_SESSION, realDate: '2026-09-19' };
/** A refusal as 1.T answers it: `details.issues` names the path, as a 400's issues do. */
const invalidBody = (code, path) => ({
    status: 422,
    body: {
        code: 'invalid_body',
        message: 'The session cannot be stored.',
        details: { issues: [{ path, code, message: 'The session cannot be stored.' }] },
    },
});
/**
 * The 422 `invalid_body` answers of `sessions.create` (1.T item T1) and `sessions.update` (T4), one
 * example each; a client reads `code` and `details`, never `message`. Session 2 holds number 2.
 */
export const EXAMPLE_SESSION_REFUSALS = {
    'sessions.create': [
        {
            ...invalidBody('number_taken', ['number']),
            request: { body: { title: 'Salt in the Wells, again', number: 2 } },
        },
        {
            ...invalidBody('number_required', ['number']),
            request: { body: { title: "The Abbot's Bargain", status: 'played' } },
        },
        { ...invalidBody('status_running', ['status']) },
        { ...invalidBody('unstorable_text', ['title']) },
    ],
    'sessions.update': [
        {
            ...invalidBody('number_taken', ['number']),
            request: { params: { id: ids.sessionSunkenChapel.id }, body: { number: 2 } },
        },
    ],
};
/** NPCs whose Loyalty is at least 30, by title. */
export const EXAMPLE_CATALOGUE_ROWS = {
    items: [
        {
            document: ref(ids.npcOrlaVenn.id, 'OrlaVennNpc00001', 'Captain Orla Venn', 'npc'),
            canonStatus: 'canon',
            updatedAt: EXAMPLE_NOW,
            values: { title: ['Captain Orla Venn'], loyalty: [70] },
        },
    ],
    next: null,
    total: 1,
};
/**
 * Guard-Captain Rell, a stub waiting to be written: its primary button opens the stub's page
 * (`docId`, N1; no block), and a stub carries no text (N2).
 */
export const EXAMPLE_INBOX_ITEM = {
    category: 'stub',
    subjectId: ids.npcGuardCaptainRell.id,
    subjectKind: 'document',
    title: 'Guard-Captain Rell',
    reason: 'Created from the @ picker in Missing Lamplighters',
    primaryAction: 'Write it',
    createdAt: EXAMPLE_NOW,
    docId: ids.npcGuardCaptainRell.id,
    blockId: null,
    text: null,
};
export const EXAMPLE_INBOX_COUNTS = {
    counts: [
        { category: 'stub', count: 1 },
        { category: 'capture', count: 1 },
        { category: 'chrono_violation', count: 1 },
        { category: 'orphaned_secret', count: 1 },
    ],
};
/** The capture `inbox.capture`'s example makes, with its text (N2) and no page (N1). */
export const EXAMPLE_CAPTURE_ITEM = {
    category: 'capture',
    subjectId: answer.captureCreated,
    subjectKind: 'capture',
    title: 'Ask Orla about the salt barrels',
    reason: 'Captured',
    primaryAction: 'File it',
    createdAt: EXAMPLE_NOW,
    docId: null,
    blockId: null,
    text: 'Ask Orla about the salt barrels',
};
const DOCKHAND_39 = ref(ids.npcDockhand39.id, 'Dockhand39Npc001', 'Dockhand 39', 'npc');
const DOCKHAND_40 = ref(ids.npcDockhand40.id, 'Dockhand40Npc001', 'Dockhand 40', 'npc');
/**
 * Dockhand 39 merged into Dockhand 40: one link moves, the title becomes an alias, and the loser's
 * one paragraph is appended to the survivor (M1).
 */
export const EXAMPLE_MERGE_PREVIEW = {
    loser: DOCKHAND_39,
    survivor: DOCKHAND_40,
    primary: 'none',
    linksRepointed: 1,
    selfLinksDropped: [],
    referencesRepointed: 2,
    aliasesAdded: ['Dockhand 39'],
    blocksMoved: 1,
};
/** Dockhand 40 after the merge, as `docs.meta` would answer it. */
export const EXAMPLE_MERGED_SURVIVOR = docMetaFrom(exampleDocument({
    id: DOCKHAND_40.id,
    fid: DOCKHAND_40.fid,
    typeId: EXAMPLE_TYPE_IDS.npc,
    typeKey: 'npc',
    title: 'Dockhand 40',
    body: { type: 'doc', content: [{ type: 'paragraph' }] },
    aliases: [{ name: 'Dockhand 39', caseSensitive: false, secret: false }],
    metaVersion: 2,
}), { capabilities: ['speaker', 'discoverable'], homeId: null });
/** Old Sef's one unlinked occurrence. */
export const EXAMPLE_LINK_ALL_PREVIEW = {
    occurrences: [EXAMPLE_UNLINKED],
    total: 1,
};
export const EXAMPLE_LINK_ALL_RESULT = {
    linked: 1,
    sourceBodyVersions: { [ids.questMissingLamplighters.id]: 2 },
};
//# sourceMappingURL=examples-ledger.js.map